import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useRecommendations } from 'api/queries/marketing-capabilities/recommendations/useRecommendations'
import crossIcon from 'assets/images/explore-learnings/cross.svg'
import historyIcon from 'assets/images/explore-learnings/history.svg'
import searchIcon from 'assets/images/explore-learnings/search.svg'
import {
  setExploreLearningsSearch,
  setExploreLearningsSearchHistory,
  setExploreLearningsSearchText,
  useExploreLearnings,
} from 'store/ExploreLearnings'
import { arrayMove } from 'utils'

interface SearchHistoryItem {
  text: string
  skill: string | null
  type?: 'skills' | 'resources' | 'community' | 'practitioners' | null
}

interface SearchBarProps {
  search?: string
  setIsOpen?: (isOpen: boolean) => void
  page?: 'skills' | 'resources' | 'community'
  onSearchStart?: () => void
}

export const SearchBar = ({ setIsOpen, page, onSearchStart }: SearchBarProps) => {
  const [showOptions, setShowOptions] = useState(false)
  const searchState = useExploreLearnings(state => state.search)
  const searchText = useExploreLearnings(state => state.searchText)
  const searchHistory = useExploreLearnings(state => state.searchHistory) as SearchHistoryItem[]

  useEffect(() => {
    searchText !== searchState && setExploreLearningsSearchText(searchState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFocus = () => {
    setShowOptions(true)
    setIsOpen && setIsOpen(true)
  }
  const handleBlur = () => {
    setShowOptions(false)
    setIsOpen && setIsOpen(false)
  }

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const makeBold = (item: string, keyword: string) => {
    if (keyword.length === 0) return item
    const lowerCasedInputValue = keyword.toLowerCase()
    const hitIndex = item.toLocaleLowerCase().indexOf(lowerCasedInputValue)
    if (hitIndex === -1) return item
    const diff = (50 - keyword.length) / 2
    const beforePosition = hitIndex - diff > 0 ? hitIndex - diff : 0
    const afterPosition =
      hitIndex + keyword.length + diff <= item.length ? hitIndex + keyword.length + diff : item.length
    const before = item.slice(beforePosition, hitIndex)
    const match = item.slice(hitIndex, hitIndex + keyword.length)
    const after = item.slice(afterPosition - (afterPosition - (hitIndex + keyword.length)), afterPosition)
    return (
      <span>
        {hitIndex < 0 && '...'}
        {before}
        <b>{match}</b>
        {after}
        {afterPosition !== item.length && '...'}
      </span>
    )
  }

  const handleChange = (searchText: string) => {
    setShowOptions(true)
    setExploreLearningsSearchText(searchText)
  }

  const handleSearch = (text?: string) => {
    onSearchStart?.()

    if (text) {
      setExploreLearningsSearchText(text)
      setExploreLearningsSearch(text)
    } else {
      if (!searchHistory.some(history => history.text.toLowerCase().trim() === searchText.toLowerCase().trim())) {
        const updatedHistory = [{ text: searchText, skill: null, type: null }, ...searchHistory].slice(0, 4)
        setExploreLearningsSearchHistory(updatedHistory)
      }
      setExploreLearningsSearch(searchText)
    }
    setIsOpen && setIsOpen(false)
    setShowOptions(false)
  }

  const { data: recommendations, isLoading } = useRecommendations()
  const sortedRecommendations = () => {
    if (isLoading) return []
    if (!recommendations) return []
    let sorted: any[] = []
    let keys: any = Object.keys(recommendations)
    keys = arrayMove(keys, keys.indexOf(page === 'community' ? 'practitioners' : page), 0)
    keys.forEach((key: string) => {
      const normalizedType = key === 'practitioners' ? 'community' : key
      const keyRec = recommendations[key].map((rec: any) => ({ ...rec, type: normalizedType }))
      sorted = [...sorted, ...keyRec]
    })
    return sorted
  }

  const navigate = useNavigate()
  const navigateToSkill = (skillId: string) => {
    onSearchStart?.()
    setExploreLearningsSearch('')
    setExploreLearningsSearchText('')
    return navigate(`/explore-learnings/skill/${skillId}`)
  }

  const navigateToResource = (resourceName: string) => {
    onSearchStart?.()
    setExploreLearningsSearch(resourceName)
    setExploreLearningsSearchText(resourceName)
    return navigate('/explore-learnings/resources')
  }

  const navigateToCommunity = (practitionerName: string) => {
    onSearchStart?.()
    setExploreLearningsSearch(practitionerName)
    setExploreLearningsSearchText(practitionerName)
    return navigate('/explore-learnings/community')
  }

  return (
    <div className="relative text-14 z-30 search-bar">
      <input
        className={`body-s w-[400px] h-[38px] px-3 py-2 pr-[52px] rounded-2xl bg-white shadow-md outline-none ${showOptions ? 'rounded-b-none' : ''}`}
        placeholder="Search for skills, resources and more"
        type="text"
        name="search"
        value={searchText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={e => {
          handleChange(e.target.value)
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        id=""
      />
      {searchText.length === 0 && searchState.length === 0 ? (
        <img
          className="absolute right-5 top-1/2 -translate-y-1/2 cursor-pointer"
          src={searchIcon}
          alt="Search Icon"
          onClick={() => {
            onSearchStart?.()
            setExploreLearningsSearch(searchText)
          }}
        />
      ) : (
        <img
          className="absolute right-5 top-[11px] cursor-pointer"
          src={crossIcon}
          alt="Cross Icon"
          onClick={() => {
            setExploreLearningsSearchText('')
            setExploreLearningsSearch('')
          }}
        />
      )}

      <div
        className={`searcher-dropdown w-[400px] p-4 absolute z-40  left-0 top-[32px] bg-white rounded-2xl rounded-t-none shadow-md ${showOptions ? 'block' : 'hidden'}`}
      >
        {searchHistory.length > 0 && (
          <ul className="mb-6">
            {searchHistory.map((item, index) => (
              <li
                key={index}
                className="body-s flex text-black py-1 cursor-pointer items-center line-clamp-1"
                onMouseDown={() => {
                  if (item.skill) {
                    navigateToSkill(item.skill)
                  } else {
                    onSearchStart?.()
                    if (item.type === 'resources') {
                      navigateToResource(item.text)
                    } else if (item.type === 'community' || item.type === 'practitioners') {
                      navigateToCommunity(item.text)
                    } else {
                      setExploreLearningsSearchText(item.text)
                      setExploreLearningsSearch(item.text)
                    }
                  }
                }}
              >
                <img src={historyIcon} alt="" className="size-4 mr-1" />
                {item.text}
              </li>
            ))}
          </ul>
        )}
        <p className="subtitle-xs text-[#9B9B9B]">RECOMMENDED</p>
        <ul className="mt-2">
          {sortedRecommendations().length > 0 &&
            sortedRecommendations().map(
              (item: { id: string; name: string; type: string }, index: any) =>
                index < 5 && (
                  <li
                    key={index}
                    className="body-s font-TCCC text-black py-1 cursor-pointer line-clamp-1"
                    onMouseDown={() => {
                      if (
                        !searchHistory.some(
                          history => history.text.toLocaleLowerCase().trim() === item.name.toLocaleLowerCase().trim(),
                        )
                      ) {
                        const normalizedType = item.type === 'practitioners' ? 'community' : item.type
                        const newHistoryItem = {
                          text: item.name,
                          skill: item.type === 'skills' ? item.id : null,
                          type: normalizedType as 'skills' | 'resources' | 'community',
                        }
                        const trimmedHistory = searchHistory.slice(0, 3)
                        setExploreLearningsSearchHistory([newHistoryItem, ...trimmedHistory])
                      }
                      window.scrollTo(0, 0)
                      if (item.type === 'skills') {
                        navigateToSkill(item.id)
                      } else if (item.type === 'resources') {
                        navigateToResource(item.name)
                      } else {
                        navigateToCommunity(item.name)
                      }
                    }}
                  >
                    {makeBold(item.name, searchText)}
                  </li>
                ),
            )}
        </ul>
      </div>
    </div>
  )
}
